import { defineComponent, h, ref, resolveComponent } from 'vue'
import { RouterLink } from 'vue-router'
import {
  CBadge,
  CSidebarNav,
  CNavItem,
  CNavGroup,
  CNavTitle,
} from '@coreui/vue'
import nav from '@/_nav.js'

const AppSidebarNav = defineComponent({
  name: 'AppSidebarNav',
  components: {
    CNavItem,
    CNavGroup,
    CNavTitle,
  },
  props: {
    clients: {
      type: Array,
      default: () => [],
    },
    loading: {
      type: Boolean,
      default: false,
    },
  },
  setup(props) {
    const navigation = ref([...nav])

    const groupByYear = (declarations, annualLiquidations) => {
      const years = new Set()

      declarations.forEach((dec) => {
        const year = Number(dec.year_balance)
        if (!isNaN(year)) years.add(year)
      })

      annualLiquidations.forEach((liq) => {
        const year = Number(liq.balanceYear)
        if (!isNaN(year)) years.add(year)
      })

      return Array.from(years)
        .sort((a, b) => b - a)
        .slice(0, 6)
    }

    const getYearItems = (year, declarations, annualLiquidations, clientId) => {
      const items = []

      const declaration = declarations.find(
        (d) => Number(d.year_balance) === year,
      )
      if (declaration) {
        items.push({
          name: `Declaración IVA`,
          to: `/declaraciones/fiscal1376/${clientId}/${declaration.id}/${declaration.cede_option}`,
          icon: 'cil-description',
        })
      }

      const liquidation = annualLiquidations.find(
        (l) => Number(l.balanceYear) === year,
      )
      if (liquidation) {
        items.push({
          name: `Declaración IRAE`,
          to: `/declaraciones/fiscal1050/${clientId}/${liquidation.id}`,
          icon: 'cil-money',
        })
      }

      return items
    }

    const renderItem = (item) => {
      if (item.name === 'Clientes') {
        if (props.loading) {
          return [
            h(
              CNavTitle,
              {
                class: 'ms-4',
              },
              {
                default: () => [
                  h(resolveComponent('CIcon'), {
                    customClassName: 'nav-icon icon-margin',
                    name: 'cil-file',
                  }),
                  'Cargando declaraciones...',
                ],
              },
            ),
          ]
        }

        return [
          h(
            CNavTitle,
            {
              class: 'ms-4',
            },
            {
              default: () => [
                h(resolveComponent('CIcon'), {
                  customClassName: 'nav-icon',
                  name: item.icon,
                }),
                'Declaraciones',
              ],
            },
          ),
          ...props.clients.map((client) =>
            h(
              CNavGroup,
              {
                visible: false,
              },
              {
                togglerContent: () => [
                  h(resolveComponent('CIcon'), {
                    customClassName: 'nav-icon',
                    name: 'cil-user',
                  }),
                  client.name,
                ],
                default: () => {
                  if (!client.declarations || !client.annualLiquidations) {
                    return []
                  }

                  const years = groupByYear(
                    client.declarations,
                    client.annualLiquidations,
                  )

                  return years.map((year) =>
                    h(
                      CNavGroup,
                      {
                        visible: false,
                      },
                      {
                        togglerContent: () => [
                          h(resolveComponent('CIcon'), {
                            customClassName: 'nav-icon',
                            name: 'cil-calendar',
                          }),
                          year.toString(),
                        ],
                        default: () => {
                          const yearItems = getYearItems(
                            year,
                            client.declarations,
                            client.annualLiquidations,
                            client.id,
                          )

                          return yearItems.map((yearItem) =>
                            h(
                              RouterLink,
                              {
                                to: yearItem.to,
                                custom: true,
                              },
                              {
                                default: (props) =>
                                  h(
                                    CNavItem,
                                    {
                                      active: props.isActive,
                                      href: props.href,
                                      onClick: () => props.navigate(),
                                    },
                                    {
                                      default: () => [
                                        h(resolveComponent('CIcon'), {
                                          customClassName: 'nav-icon',
                                          name: yearItem.icon,
                                        }),
                                        yearItem.name,
                                      ],
                                    },
                                  ),
                              },
                            ),
                          )
                        },
                      },
                    ),
                  )
                },
              },
            ),
          ),
        ]
      }

      if (item.items) {
        return h(
          CNavGroup,
          {},
          {
            togglerContent: () => [
              h(resolveComponent('CIcon'), {
                customClassName: 'nav-icon',
                name: item.icon,
              }),
              item.name,
            ],
            default: () => item.items.map((child) => renderItem(child)),
          },
        )
      }

      return item.to
        ? h(
            RouterLink,
            {
              to: item.to,
              custom: true,
            },
            {
              default: (props) =>
                h(
                  CNavItem,
                  {
                    active: props.isActive,
                    href: props.href,
                    onClick: () => props.navigate(),
                  },
                  {
                    default: () => [
                      item.icon &&
                        h(resolveComponent('CIcon'), {
                          customClassName: 'nav-icon',
                          name: item.icon,
                        }),
                      item.name,
                      item.badge &&
                        h(
                          CBadge,
                          {
                            class: 'ms-auto',
                            color: item.badge.color,
                          },
                          {
                            default: () => item.badge.text,
                          },
                        ),
                    ],
                  },
                ),
            },
          )
        : h(
            resolveComponent(item.component),
            {},
            {
              default: () => item.name,
            },
          )
    }

    return () =>
      h(
        CSidebarNav,
        {},
        {
          default: () => navigation.value.flatMap((item) => renderItem(item)),
        },
      )
  },
})
export { AppSidebarNav }
